import { useEffect, useState } from "react";
import { Plus, Trash2, X, Pencil, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyCat = { name: "", kind: "ambos" };

const KINDS = [
  { id: "receita", label: "Receita" },
  { id: "despesa", label: "Despesa" },
  { id: "ambos", label: "Ambos" },
];

const kindStyle = {
  receita: "text-nx-yellow border-nx-yellow/50",
  despesa: "text-zinc-300 border-zinc-600",
  ambos: "text-blue-300 border-blue-500/40",
};

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function Cadastros() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyCat);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCat);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, kind: c.kind });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { data } = await api.patch(`/categories/${editing.id}`, form);
        setCategories((cs) => cs.map((c) => (c.id === editing.id ? data : c)));
        toast.success("Categoria atualizada");
      } else {
        const { data } = await api.post("/categories", form);
        setCategories((cs) => [...cs, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Categoria cadastrada");
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyCat);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      setCategories((cs) => cs.filter((c) => c.id !== id));
      toast.success("Categoria removida");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="cadastros-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">Base de dados</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="cadastros-title">Cadastros</h1>
        </div>
        <button
          onClick={openCreate}
          data-testid="category-add-button"
          className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
        >
          <Plus className="w-4 h-4" /> Nova Categoria
        </button>
      </div>

      <div className="nx-card" data-testid="categories-table">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-zinc-800">
          <h2 className="font-syne text-lg font-bold mr-auto flex items-center gap-2">
            <Tag className="w-4 h-4 text-nx-yellow" /> Categorias financeiras
          </h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar categoria..."
            data-testid="category-search-input"
            className={`${inputCls} !w-64`}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                {["Nome", "Aplicável a", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`category-row-${c.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{c.name}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-mono uppercase border rounded-full px-2.5 py-1 ${kindStyle[c.kind]}`}>
                      {KINDS.find((k) => k.id === c.kind)?.label || c.kind}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(c)} title="Editar" data-testid={`category-edit-${c.id}`} className="text-zinc-400 hover:text-nx-yellow transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {user?.role === "admin" && (
                        <button onClick={() => remove(c.id)} title="Excluir" data-testid={`category-delete-${c.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-10 text-center text-zinc-500">Nenhuma categoria encontrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="category-form-modal">
          <form onSubmit={save} className="glass rounded-3xl p-7 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">{editing ? "Editar Categoria" : "Nova Categoria"}</h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} data-testid="category-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Nome *">
                <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Ex.: Tráfego, Software" data-testid="category-name-input" />
              </Field>
              <Field label="Aplicável a">
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls} data-testid="category-kind-select">
                  {KINDS.map((k) => (
                    <option key={k.id} value={k.id}>{k.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <button type="submit" data-testid="category-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              {editing ? "Salvar alterações" : "Cadastrar categoria"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
